// ============================================================================
// 🛠️ FUNÇÕES DE FORMATAÇÃO - FINANCEIRO
// ============================================================================

/**
 * Formata um valor para moeda BRL (R$)
 * @param {number|string} valor - Valor a ser formatado
 * @param {boolean} showSymbol - Se deve mostrar o símbolo R$
 * @param {number} minDigits - Mínimo de dígitos decimais
 * @param {number} maxDigits - Máximo de dígitos decimais
 * @returns {string} Valor formatado (ex: R$ 1.234,56)
 */
export const formatMoney = (valor, showSymbol = true, minDigits = 2, maxDigits = 2) => {
  if (valor === undefined || valor === null || valor === '') return showSymbol ? 'R$ 0,00' : '0,00';
  
  const num = Number(valor);
  if (isNaN(num)) return showSymbol ? 'R$ 0,00' : '0,00';
  
  const formatted = num.toLocaleString('pt-BR', {
    minimumFractionDigits: minDigits,
    maximumFractionDigits: maxDigits
  });
  
  return showSymbol ? `R$ ${formatted}` : formatted;
};

/**
 * Formata uma data para o padrão brasileiro (dd/mm/aaaa)
 * @param {string} dateString - Data no formato YYYY-MM-DD
 * @param {boolean} short - Se deve retornar apenas dia/mês (dd/mm)
 * @returns {string} Data formatada
 */
export const formatDate = (dateString, short = false) => {
  if (!dateString) return short ? '--/--' : 'Data não informada';
  
  try {
    // Forçar horário meio-dia para evitar problemas de timezone
    const date = new Date(dateString + 'T12:00:00');
    
    if (short) {
      return date.toLocaleDateString('pt-BR').substring(0, 5);
    }
    
    return date.toLocaleDateString('pt-BR');
  } catch (error) {
    console.error('Erro ao formatar data:', error);
    return short ? '--/--' : 'Data inválida';
  }
};

/**
 * Formata um mês no formato YYYY-MM para exibição (ex: 2024-03 → Março/2024)
 * @param {string} monthString - Mês no formato YYYY-MM
 * @returns {string} Mês formatado
 */
export const formatMonth = (monthString) => {
  if (!monthString || !monthString.includes('-')) return monthString;
  
  try {
    const [year, month] = monthString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    
    return date.toLocaleDateString('pt-BR', { 
      month: 'long', 
      year: 'numeric' 
    });
  } catch (error) {
    return monthString;
  }
};

/**
 * Abrevia uma categoria para mobile
 * @param {string} category - Nome da categoria
 * @param {number} maxLength - Tamanho máximo
 * @returns {string} Categoria abreviada
 */
export const abbreviateCategory = (category, maxLength = 12) => {
  if (!category) return '';
  if (category.length <= maxLength) return category;
  
  // Casos especiais para mobile
  if (category === 'Venda de Imóvel') return 'Venda';
  if (category === 'Comissão de Venda') return 'Comissão';
  if (category === 'Comissão de Aluguel') return 'Com. Alug.';
  if (category === 'Taxa de Administração') return 'Taxa Adm.';
  if (category === 'Sinal / Entrada') return 'Sinal';
  if (category === 'Anúncios / Tráfego Pago') return 'Anúncios';
  
  return category.substring(0, maxLength) + '...';
};

/**
 * Retorna a classe de cor baseada no tipo/categoria
 * @param {Object} item - Item financeiro
 * @param {boolean} isMobile - Se é mobile
 * @returns {string} Classes CSS
 */
export const getTypeColorClass = (item, isMobile = false) => {
  if (!item) return '';
  
  if (item.category === 'Venda de Imóvel') {
    return isMobile ? 'bg-purple-100 text-purple-700' : 'bg-purple-100 text-purple-700';
  }
  if (item.category === 'Aluguel') {
    return isMobile ? 'bg-blue-100 text-blue-700' : 'bg-blue-100 text-blue-700';
  }
  if (item.type === 'receita') {
    return isMobile ? 'bg-emerald-100 text-emerald-700' : 'bg-emerald-100 text-emerald-700';
  }
  return isMobile ? 'bg-rose-100 text-rose-700' : 'bg-rose-100 text-rose-700';
};

/**
 * Retorna o texto do tipo abreviado para mobile
 * @param {Object} item - Item financeiro
 * @returns {string} Tipo abreviado
 */
export const getTypeShortText = (item) => {
  if (!item) return '';
  
  if (item.category === 'Venda de Imóvel') return 'Venda';
  if (item.category === 'Aluguel') return 'Alug.';
  if (item.type === 'receita') return 'Rec.';
  return 'Desp.';
};

/**
 * Retorna o texto do tipo completo
 * @param {Object} item - Item financeiro
 * @returns {string} Tipo completo
 */
export const getTypeFullText = (item) => {
  if (!item) return '';
  
  if (item.category === 'Venda de Imóvel') return 'Venda';
  if (item.category === 'Aluguel') return 'Aluguel';
  if (item.type === 'receita') return 'Receita';
  return 'Despesa';
};

// Exportar todas as funções em um objeto para facilitar
export default {
  money: formatMoney,
  date: formatDate,
  month: formatMonth,
  abbreviateCategory,
  getTypeColorClass,
  getTypeShortText,
  getTypeFullText
};